// src/utils/idGenerator.js
import { supabase } from '../lib/supabase';

/**
 * Fetch the factory short_code from the factories table by factory name.
 * Falls back to an 8-char uppercase abbreviation only if not found.
 */
export async function getFactoryShortCode(factoryName) {
  if (!factoryName) return 'FACTORY';
  try {
    const { data, error } = await supabase
      ?.from('factories')
      ?.select('short_code')
      ?.ilike('name', factoryName?.trim())
      ?.maybeSingle();
    if (!error && data?.short_code) {
      return data?.short_code?.trim()?.toUpperCase();
    }
  } catch (_) { /* fallback below */ }
  // Fallback: abbreviate (should rarely happen)
  return factoryName?.replace(/\s+/g, '')?.toUpperCase()?.slice(0, 8) || 'FACTORY';
}

/**
 * Generate readable_offer_id for the buyer dashboard
 * Example: RPSSKL-260421-001, SKSAPL-260421-002
 */
export function formatReadableOfferId(factoryShortCode, date, seq = 1) {
  const dateStr = date?.toISOString()?.slice(2, 10)?.replace(/-/g, ''); // YYMMDD
  return `${factoryShortCode}-${dateStr}-${seq?.toString()?.padStart(3, '0')}`;
}

/**
 * Generate readable_booking_id for buyer purchases / truck allotment
 * Example: IDON-RPSSKL-260421-25 or IDON-RPSSKL-260421-25-02
 */
export function formatReadableBookingId(buyerCode, factoryShortCode, date, quantity) {
  const dateStr = date?.toISOString()?.slice(2, 10)?.replace(/-/g, ''); // YYMMDD
  const qty = Math.floor(quantity);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const ist = new Date(date.getTime() + istOffset);
  const hhmm = String(ist.getUTCHours()).padStart(2, '0') + String(ist.getUTCMinutes()).padStart(2, '0');
  return `${buyerCode}-${factoryShortCode}-${dateStr}-${qty}-${hhmm}`;
}

/**
 * Parse readable_booking_id back into components
 * Useful for truck allotment, dispatch, and backend logic
 */
export function parseReadableBookingId(bookingId) {
  if (!bookingId) return null;

  const parts = bookingId?.split('-');
  if (parts?.length < 4) return null;

  const buyerCode = parts?.[0];
  const factoryShortCode = parts?.[1];
  const dateStr = parts?.[2];
  const tonnage = parseFloat(parts?.[3]);
  const sequence = parts?.length > 4 ? parseInt(parts?.[4], 10) : 1;

  return {
    buyerCode,
    factoryShortCode,
    dateStr,
    tonnage,
    sequence
  };
}

/**
 * Helper to get current date
 */
export function getToday() {
  return new Date();
}

